import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { Flag } from "@/components/ui/flag";
import PlanMove from "@/components/pricing2026/PlanMove";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRY_EXAMPLES, DEFAULT_COUNTRY, type CountryExample, type CountryId } from "@/content/pricing/countries";
import { fill, type PricingLocale } from "@/content/pricing";
import { browserRegion, countryExample, exampleForRegion, monthlyExamples, orderExamples } from "@/lib/pricing/changes2026";
import { formatMoney, ratePercent } from "@/lib/pricing/orderFee";

// Country examples on /pricing-changes-2026: a typical order and three monthly sales levels in local
// currency, today vs from 1 November. The default country comes from `?country=<id>` (e.g.
// ?country=my, handy for sharing a link) or else the browser's locale region (the same signal the
// fees-by-country table uses: there's no server-side geo lookup), falling back to the UK.
// Data and sources: src/content/pricing/countries.ts. Maths: src/lib/pricing/changes2026.ts.

const isCountryId = (v: string | null): v is CountryId => !!v && COUNTRY_EXAMPLES.some((c) => c.id === v);

/** Whole local-currency amounts for monthly figures, written the country's way: "£5,000", "COP 2.100.000". */
const wholeMoney = (n: number, c: CountryExample) =>
  `${c.currency.prefix}${new Intl.NumberFormat(c.moneyLocale, { maximumFractionDigits: 0 }).format(n)}`;

const CountryExamples = ({ loc }: { loc: PricingLocale }) => {
  const { copy, numberLocale } = loc;
  const t = copy.changes.countries;
  const [params] = useSearchParams();
  const fromParam = params.get("country");
  const [id, setId] = useState<CountryId>(isCountryId(fromParam) ? fromParam : DEFAULT_COUNTRY);
  const picked = useRef(isCountryId(fromParam));

  useEffect(() => {
    if (picked.current || typeof navigator === "undefined") return;
    const region = browserRegion([...(navigator.languages ?? []), navigator.language]);
    setId(exampleForRegion(region));
  }, []);

  const country = countryExample(id);
  const orders = useMemo(() => orderExamples(country), [country]);
  const months = useMemo(() => monthlyExamples(country), [country]);
  // Money is written the country's way; percentages follow the page language.
  const money = (minor: number) => formatMoney(minor, country.currency, country.moneyLocale);
  const pct = (share: number) => ratePercent(share, numberLocale);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="mb-2 inline-block rounded-full bg-hawk-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-hawk-orange">
            {t.examplesBadge}
          </span>
          <h2 id="countries-heading" className="mb-2 text-3xl font-display font-bold md:text-4xl">
            {t.heading}
          </h2>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>
        <div className="w-full sm:w-72">
          <label htmlFor="country-example" className="mb-1.5 block text-sm font-semibold">
            {t.selectLabel}
          </label>
          <Select
            value={id}
            onValueChange={(v) => {
              picked.current = true;
              if (isCountryId(v)) setId(v);
            }}
          >
            <SelectTrigger id="country-example" className="bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_EXAMPLES.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="flex items-center gap-2">
                    <Flag country={c.flag} />
                    {copy.common.countries[c.id]} ({c.currency.code})
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1.15fr_1fr]">
        {/* A typical order */}
        <div className="rounded-2xl bg-card p-6 shadow-lg md:p-8" aria-live="polite">
          <h3 className="mb-1 text-xl font-display font-bold">
            {fill(t.orderHeading, { amount: money(orders[0].order) })}
          </h3>
          <p className="mb-5 text-sm text-muted-foreground">
            {fill(t.orderSubtitle, {
              inCountry: copy.common.inCountry[country.id],
              fee: country.cardFeeLabel,
              kind: t.kind[country.cardFeeKind],
            })}{" "}
            <span className="whitespace-nowrap">(−{money(orders[0].cardFee)})</span>
          </p>
          <div className="hidden grid-cols-[1.3fr_1fr_1fr] gap-3 border-b border-border pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
            <span>{t.colPlan}</span>
            <span className="text-right">{t.colCommission}</span>
            <span className="text-right">{t.colKeep}</span>
          </div>
          <ul className="divide-y divide-border">
            {orders.map((o) => (
              <li key={o.from.id} className="grid grid-cols-2 gap-x-3 gap-y-1 py-3 text-sm sm:grid-cols-[1.3fr_1fr_1fr] sm:items-center">
                <span className="col-span-2 font-semibold sm:col-span-1">
                  <PlanMove from={o.from} copy={copy} />
                </span>
                <span className="text-muted-foreground sm:text-right">
                  <span className="block text-xs sm:hidden">{t.colCommission}</span>
                  <span className="tabular-nums">
                    <span className="sr-only">{copy.common.today}: </span>
                    <s className="opacity-70">{money(o.commissionToday)}</s>{" "}
                    <span className="sr-only">{copy.common.fromChangeDate}: </span>
                    {money(o.commissionNext)}
                  </span>
                </span>
                <span className="text-right">
                  <span className="block text-xs text-muted-foreground sm:hidden">{t.colKeep}</span>
                  <span className="font-display text-lg font-bold text-hawk-orange tabular-nums">{money(o.keepNext)}</span>
                  <span className="block text-xs text-muted-foreground tabular-nums">
                    {fill(t.perOrderMore, { amount: money(o.keepNext - o.keepToday) })}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* A month of sales */}
        <div className="rounded-2xl bg-card p-6 shadow-lg md:p-8" aria-live="polite">
          <h3 className="mb-1 text-xl font-display font-bold">{t.monthlyHeading}</h3>
          <p className="mb-5 text-sm text-muted-foreground">{t.monthlySubtitle}</p>
          <ul className="space-y-4">
            {months.map((m) => (
              <li key={m.salesGbp} className="rounded-xl border border-border p-4">
                <p className="mb-2 text-sm font-semibold">
                  {fill(t.level, { amount: wholeMoney(m.salesLocal, country) })}
                </p>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-muted-foreground">
                    <span className="text-xs">{copy.common.today} </span>
                    <s className="font-semibold tabular-nums">{pct(m.today.share)}</s>
                    <span className="text-xs"> {fill(t.onPlan, { plan: m.today.plan })}</span>
                  </span>
                  <ArrowRight size={14} className="self-center text-hawk-orange" aria-hidden="true" />
                  <span>
                    <span className="text-xs text-muted-foreground">{copy.common.fromChangeDate} </span>
                    <span className="font-display text-2xl font-bold text-hawk-orange tabular-nums">{pct(m.next.share)}</span>
                    <span className="text-xs text-muted-foreground"> {fill(t.onPlan, { plan: m.next.plan })}</span>
                  </span>
                </div>
                <p className="mt-1 text-sm">{fill(t.keepMore, { amount: wholeMoney(m.keepMoreLocal, country) })}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="mt-6 flex items-start gap-2 text-xs text-muted-foreground">
        <Info size={14} className="mt-0.5 flex-shrink-0 text-hawk-orange" />
        {t.disclaimer}
      </p>
    </div>
  );
};

export default CountryExamples;
